import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { SSMLTag, SSMLTagsColors } from "shared/constants";
import {
  handleSSMLValueClick,
  isEditorEmpty,
  useEditorSelection,
} from "shared/utils/tts-editor-helper";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import TranslateIcon from "@mui/icons-material/Translate";
import { LanguageMenuItems } from "../../components/LanguageMenuItems";
import { useIsActiveSSMLTag } from "shared/hooks/useSSMLTag";
import CustomIconTextButtonMenu from "../../components/CustomIconTextButtonMenu";
import CustomSlider from "shared/components/CustomSlider";
import { Box, Stack, Typography } from "@mui/material";
import { accentLevelLabels } from "shared/constants";
import SSMLPreview from "features/TTS/TTSCustomEditor/components/SSMLPreview";

interface LanguageMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
  disabled?: boolean;
}

const DEFAULT_ACCENT_INDEX = 0;
const DEFAULT_LANGUAGE = null;

export default function LanguageMenu({
  editorRef,
  disabled,
}: LanguageMenuProps): JSX.Element {
  const { t } = useTranslation();
  const isLanguageTagActive = useIsActiveSSMLTag(SSMLTag.Language);
  const editorEmpty = isEditorEmpty(editorRef);
  const hasSelection = useEditorSelection(editorRef);
  const [accentSliderValue, setAccentSliderValue] =
    useState(DEFAULT_ACCENT_INDEX);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    DEFAULT_LANGUAGE
  );

  const isSelectionDisabled = useSSMLMenuDisabled(editorRef, SSMLTag.Language);
  const isDisabled =
    disabled ||
    editorEmpty ||
    isSelectionDisabled ||
    (!hasSelection && !isLanguageTagActive);

  const hasChanges = isLanguageTagActive
    ? selectedLanguage !==
        getCurrentSSMLValue(editorRef, SSMLTag.Language)?.split(", ")[0] ||
      accentLevelLabels[accentSliderValue] !==
        (getCurrentSSMLValue(editorRef, SSMLTag.Language)?.split(", ")[1] ??
          "none")
    : !!selectedLanguage;

  useEffect(() => {
    if (isLanguageTagActive && editorRef.current?.editor) {
      const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Language);
      if (currentValue) {
        const [lang, accent] = currentValue.split(", ");
        setSelectedLanguage(lang ?? null);
        if (accent) {
          const index = accentLevelLabels.indexOf(accent);
          setAccentSliderValue(index !== -1 ? index : DEFAULT_ACCENT_INDEX);
        } else {
          setAccentSliderValue(DEFAULT_ACCENT_INDEX);
        }
      }
    }
  }, [isLanguageTagActive, editorRef]);

  const handleLanguageClick = (language: string) => {
    setSelectedLanguage(language);
  };

  const handleApply = () => {
    if (!editorRef.current || !selectedLanguage) return;

    const accent = accentLevelLabels[accentSliderValue];
    const value =
      accent === "none" ? selectedLanguage : `${selectedLanguage}, ${accent}`;

    handleSSMLValueClick(
      editorRef,
      SSMLTag.Language,
      value,
      SSMLTagsColors.LanguageTag,
      [SSMLTag.Language]
    );
  };

  const handleDialogClose = () => {
    if (!isLanguageTagActive) {
      setSelectedLanguage(DEFAULT_LANGUAGE);
      setAccentSliderValue(DEFAULT_ACCENT_INDEX);
    }
  };

  const handleDialogOpen = () => {
    if (isLanguageTagActive) {
      const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Language);
      if (currentValue) {
        const [lang, accent] = currentValue.split(", ");
        setSelectedLanguage(lang ?? null);
        if (accent) {
          const index = accentLevelLabels.indexOf(accent);
          setAccentSliderValue(index !== -1 ? index : DEFAULT_ACCENT_INDEX);
        } else {
          setAccentSliderValue(DEFAULT_ACCENT_INDEX);
        }
      }
    } else {
      setSelectedLanguage(DEFAULT_LANGUAGE);
      setAccentSliderValue(DEFAULT_ACCENT_INDEX);
    }
  };

  return (
    <CustomIconTextButtonMenu
      useDialog
      componentId="language"
      menuTitle={t("tts.toolbarMenu.languageMenu.title")}
      dialogHeader={t("tts.toolbarMenu.languageMenu.title")}
      icon={TranslateIcon}
      editorRef={editorRef}
      width="750px"
      isActive={isLanguageTagActive}
      isEditorEmpty={editorEmpty}
      shouldBeDisabled={isDisabled}
      isSelectionDisabled={isSelectionDisabled}
      tagType={SSMLTag.Language}
      showChevronIcon
      showClearButton={isLanguageTagActive}
      onDialogPrimaryButtonClick={handleApply}
      isDialogPrimaryButtonDisabled={
        isLanguageTagActive ? !hasChanges : !selectedLanguage
      }
      onDialogClose={handleDialogClose}
      onDialogOpen={handleDialogOpen}
      footerText={
        <SSMLPreview
          type="language"
          languageAttributes={{
            language: selectedLanguage ?? undefined,
            accent: accentLevelLabels[accentSliderValue],
          }}
        />
      }
    >
      <LanguageMenuItems
        onLanguageClick={handleLanguageClick}
        currentLanguage={selectedLanguage}
      />

      <Stack direction="row" justifyContent="space-between" paddingY={2}>
        <Typography variant="subtitle1">
          {t("tts.toolbarMenu.languageMenu.accentAttribute")}
        </Typography>
      </Stack>
      <Box sx={{ ml: 4, mt: 2, mb: 2, width: "92%" }}>
        <CustomSlider
          value={accentSliderValue}
          defaultValue={DEFAULT_ACCENT_INDEX}
          step={1}
          minLabel="none"
          maxLabel="x-strong"
          labels={accentLevelLabels}
          onSliderChange={setAccentSliderValue}
          showCheckButton={false}
          isVolumeMenu
          showAllLabels={true}
          id="language-accent-slider"
        />
      </Box>
    </CustomIconTextButtonMenu>
  );
}
